import type { GridColDef } from '@mui/x-data-grid';
import type { GridColumnGroupingModel } from '@mui/x-data-grid-pro';
import type { UsageReportV2CustomFieldName, UsageReportV2ReportItem } from '@vy/types';
import { nullToUndefined, whenDefined } from '@vy/null';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import {
  formatIntervalMonths,
  formatNumber,
  formatReportDate,
  formatTierTo,
  splitPascalCase,
} from '../usage-report-v2-format-helpers';

function buildBaseColumns(): GridColDef<UsageReportV2ReportItem>[] {
  return [
    {
      field: 'customer.name',
      headerName: 'Customer Name',
      minWidth: 180,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.customer.name,
      renderCell: ({ row }) => <VyTableCell value={row.customer.name} />,
    },
    {
      field: 'customer.id',
      headerName: 'Customer ID',
      minWidth: 140,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.customer.id,
      renderCell: ({ row }) => <VyTableCell value={row.customer.id} />,
    },
    {
      field: 'customer.externalId',
      headerName: 'External ID',
      minWidth: 120,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.customer.externalId ?? '',
      renderCell: ({ row }) => <VyTableCell value={row.customer.externalId ?? ''} />,
    },
  ];
}

function buildCustomerCfColumns(
  customerCustomFieldNames: UsageReportV2CustomFieldName[],
): GridColDef<UsageReportV2ReportItem>[] {
  return customerCustomFieldNames.map((cfName) => ({
    field: `customerCf.${cfName.id}`,
    headerName: cfName.name,
    minWidth: 140,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    customField: true,
    valueGetter: (_value, row) => {
      const rawValue = row.customerCustomFieldValues?.[cfName.id];
      return whenDefined(rawValue, String, '');
    },
    renderCell: ({ value }) => <VyTableCell value={value as string} />,
  }));
}

function buildContractColumns(): GridColDef<UsageReportV2ReportItem>[] {
  return [
    {
      field: 'contract.name',
      headerName: 'Contract Name',
      minWidth: 160,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.contract.name,
      renderCell: ({ row }) => <VyTableCell value={row.contract.name} />,
    },
    {
      field: 'contract.id',
      headerName: 'Contract ID',
      minWidth: 140,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.contract.id,
      renderCell: ({ row }) => <VyTableCell value={row.contract.id} />,
    },
    {
      field: 'contract.startDate',
      headerName: 'Contract Start Date',
      minWidth: 140,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.contract.startDate,
      renderCell: ({ row }) => (
        <VyTableCell value={formatReportDate(row.contract.startDate)} />
      ),
    },
    {
      field: 'contract.daysToEnd',
      headerName: 'Days to end',
      minWidth: 100,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.contract.daysToEnd ?? '',
      renderCell: ({ row }) => (
        <VyTableCell value={formatNumber(nullToUndefined(row.contract.daysToEnd))} />
      ),
    },
    {
      field: 'contract.endDate',
      headerName: 'Contract End Date',
      minWidth: 140,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.contract.endDate,
      renderCell: ({ row }) => (
        <VyTableCell
          value={row.contract.endDate ? formatReportDate(row.contract.endDate) : ''}
        />
      ),
    },
    {
      field: 'contract.durationMonths',
      headerName: 'Contract Duration',
      minWidth: 130,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.contract.durationMonths ?? 'EverGreen',
      renderCell: ({ row }) => (
        <VyTableCell
          value={
            whenDefined(row.contract.durationMonths, String, 'EverGreen')
          }
        />
      ),
    },
    {
      field: 'contract.status',
      headerName: 'Contract Status',
      minWidth: 120,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.contract.status,
      renderCell: ({ row }) => <VyTableCell value={row.contract.status} />,
    },
  ];
}

function buildContractCfColumns(
  contractCustomFieldNames: UsageReportV2CustomFieldName[],
): GridColDef<UsageReportV2ReportItem>[] {
  return contractCustomFieldNames.map((cfName) => ({
    field: `contractCf.${cfName.id}`,
    headerName: cfName.name,
    minWidth: 140,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    customField: true,
    valueGetter: (_value, row) => {
      const rawValue = row.contractCustomFieldValues?.[cfName.id];
      return whenDefined(rawValue, String, '');
    },
    renderCell: ({ value }) => <VyTableCell value={value as string} />,
  }));
}

function buildProductColumns(): GridColDef<UsageReportV2ReportItem>[] {
  return [
    {
      field: 'product.name',
      headerName: 'Product Name',
      minWidth: 160,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.product.name,
      renderCell: ({ row }) => <VyTableCell value={row.product.name} />,
    },
    {
      field: 'product.id',
      headerName: 'Product ID',
      minWidth: 140,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.product.id,
      renderCell: ({ row }) => <VyTableCell value={row.product.id} />,
    },
    {
      field: 'product.description',
      headerName: 'Product Description',
      minWidth: 160,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.product.description ?? '',
      renderCell: ({ row }) => <VyTableCell value={row.product.description ?? ''} />,
    },
    {
      field: 'product.productGroupName',
      headerName: 'Product Group Name',
      minWidth: 160,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.product.productGroupName ?? '',
      renderCell: ({ row }) => <VyTableCell value={row.product.productGroupName ?? ''} />,
    },
    {
      field: 'product.pricingModel',
      headerName: 'Pricing Type',
      minWidth: 130,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => splitPascalCase(row.product.pricingModel),
      renderCell: ({ row }) => (
        <VyTableCell value={splitPascalCase(row.product.pricingModel)} />
      ),
    },
    {
      field: 'product.chargingCadenceMonths',
      headerName: 'Period (Cadence)',
      minWidth: 130,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => formatIntervalMonths(row.product.chargingCadenceMonths),
      renderCell: ({ row }) => (
        <VyTableCell value={formatIntervalMonths(row.product.chargingCadenceMonths)} />
      ),
    },
    {
      field: 'product.billingIntervalMonths',
      headerName: 'Billing Interval',
      minWidth: 130,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => formatIntervalMonths(row.product.billingIntervalMonths),
      renderCell: ({ row }) => (
        <VyTableCell value={formatIntervalMonths(row.product.billingIntervalMonths)} />
      ),
    },
  ];
}

function buildUsageModificationColumns(): GridColDef<UsageReportV2ReportItem>[] {
  return [
    {
      field: 'product.commitmentUnits',
      headerName: 'Commitment (Units)',
      minWidth: 140,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.product.commitmentUnits ?? '',
      renderCell: ({ row }) => (
        <VyTableCell value={formatNumber(nullToUndefined(row.product.commitmentUnits))} />
      ),
    },
    {
      field: 'product.accumulationIntervalMonths',
      headerName: 'Commitment Reset',
      minWidth: 140,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => formatIntervalMonths(row.product.accumulationIntervalMonths),
      renderCell: ({ row }) => (
        <VyTableCell value={formatIntervalMonths(row.product.accumulationIntervalMonths)} />
      ),
    },
    {
      field: 'tier.index',
      headerName: 'Tier #',
      minWidth: 80,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.tier.index,
      renderCell: ({ row }) => <VyTableCell value={String(row.tier.index)} />,
    },
    {
      field: 'tier.from',
      headerName: 'Tiers Quantity (FROM)',
      minWidth: 140,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.tier.from ?? '',
      renderCell: ({ row }) => (
        <VyTableCell value={formatNumber(nullToUndefined(row.tier.from))} />
      ),
    },
    {
      field: 'tier.to',
      headerName: 'Tiers Quantity (TO)',
      minWidth: 140,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => formatTierTo(row.tier),
      renderCell: ({ row }) => <VyTableCell value={formatTierTo(row.tier)} />,
    },
    {
      field: 'tier.bulkAmount',
      headerName: 'Bulk Amount (Units)',
      minWidth: 140,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.tier.bulkAmount ?? '',
      renderCell: ({ row }) => (
        <VyTableCell value={formatNumber(nullToUndefined(row.tier.bulkAmount))} />
      ),
    },
  ];
}

function buildUsageColumns(): GridColDef<UsageReportV2ReportItem>[] {
  return [
    {
      field: 'period',
      headerName: 'Usage Period',
      minWidth: 200,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => `${formatReportDate(row.period.startTime)} - ${formatReportDate(row.period.endTime)}`,
      renderCell: ({ row }) => (
        <VyTableCell
          value={`${formatReportDate(row.period.startTime)} - ${formatReportDate(row.period.endTime)}`}
        />
      ),
    },
    {
      field: 'totalConsumed',
      headerName: 'Total Consumed (Contract Start - Today)',
      minWidth: 200,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.totalConsumed,
      renderCell: ({ row }) => <VyTableCell value={formatNumber(row.totalConsumed)} />,
    },
  ];
}

export function buildUsageReportV2Columns(
  customerCustomFieldNames: UsageReportV2CustomFieldName[],
  contractCustomFieldNames: UsageReportV2CustomFieldName[],
): GridColDef<UsageReportV2ReportItem>[] {
  return [
    ...buildBaseColumns(),
    ...buildCustomerCfColumns(customerCustomFieldNames),
    ...buildContractColumns(),
    ...buildContractCfColumns(contractCustomFieldNames),
    ...buildProductColumns(),
    ...buildUsageModificationColumns(),
    ...buildUsageColumns(),
  ];
}

export function buildUsageReportV2ColumnGroups(
  customerCustomFieldNames: UsageReportV2CustomFieldName[],
  contractCustomFieldNames: UsageReportV2CustomFieldName[],
): GridColumnGroupingModel {
  return [
    {
      groupId: 'customer',
      headerName: 'Customer',
      headerAlign: 'left',
      children: [
        ...buildBaseColumns().map(({ field }) => ({ field })),
        ...buildCustomerCfColumns(customerCustomFieldNames).map(({ field }) => ({ field })),
      ],
    },
    {
      groupId: 'contract',
      headerName: 'Contract',
      headerAlign: 'left',
      children: [
        ...buildContractColumns().map(({ field }) => ({ field })),
        ...buildContractCfColumns(contractCustomFieldNames).map(({ field }) => ({ field })),
      ],
    },
    {
      groupId: 'product',
      headerName: 'Product',
      headerAlign: 'left',
      children: buildProductColumns().map(({ field }) => ({ field })),
    },
    {
      groupId: 'usage-modifications',
      headerName: 'Usage Modifications',
      headerAlign: 'left',
      children: buildUsageModificationColumns().map(({ field }) => ({ field })),
    },
    {
      groupId: 'usage',
      headerName: 'Usage',
      headerAlign: 'left',
      children: buildUsageColumns().map(({ field }) => ({ field })),
    },
  ];
}
